"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getParentCandidates = exports.useGatewayLink = void 0;
const react_1 = require("react");
const mutations_1 = require("../graphql/mutations");
const types_1 = require("../types");
const amplifyBootstrap_1 = require("../utils/amplifyBootstrap");
/**
 * Amplify v6 resolves the promise even when the GraphQL response carries
 * `errors[]` with `data: null`, so a resolver-level failure looks like success
 * unless it's checked explicitly. Same guard as `useSensors`' mutations.
 */
function throwIfGraphqlErrors(response, op) {
    if (response?.errors?.length) {
        console.error(`GraphQL errors in ${op}:`, response.errors);
        throw new Error(response.errors[0]?.message || `Failed: ${op}`);
    }
}
/**
 * Caller layer for the `linkGateway` / `unlinkGateway` mutations.
 *
 * Linking is deliberately NOT part of `createHub`/`updateHub`: `parentHubId`
 * is absent from both inputs, so this mutation is the only way a link is ever
 * made. It reconciles UserHubRelations as a side effect, which is what makes a
 * gateway's sensors and alerts reachable by the parent's owners.
 */
const useGatewayLink = () => {
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const link = (0, react_1.useCallback)(async (gatewayHubId, parentHubId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.linkGateway,
                variables: { gatewayHubId, parentHubId },
            });
            throwIfGraphqlErrors(response, 'linkGateway');
            return true;
        }
        catch (err) {
            const wrapped = err instanceof Error ? err : new Error(String(err));
            console.error('Error linking gateway:', wrapped);
            setError(wrapped);
            throw wrapped;
        }
        finally {
            setLoading(false);
        }
    }, []);
    const unlink = (0, react_1.useCallback)(async (gatewayHubId) => {
        setLoading(true);
        setError(null);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.unlinkGateway,
                variables: { gatewayHubId },
            });
            throwIfGraphqlErrors(response, 'unlinkGateway');
            return true;
        }
        catch (err) {
            const wrapped = err instanceof Error ? err : new Error(String(err));
            console.error('Error unlinking gateway:', wrapped);
            setError(wrapped);
            throw wrapped;
        }
        finally {
            setLoading(false);
        }
    }, []);
    return { link, unlink, loading, error };
};
exports.useGatewayLink = useGatewayLink;
/**
 * Hubs a gateway may be attached to: real hubs only.
 *
 * A gateway cannot parent another gateway — `linkGateway` rejects it — so
 * offering one as a choice would produce an error the user can't act on.
 */
const getParentCandidates = (hubs) => hubs.filter((hub) => !(0, types_1.isGatewayHub)(hub));
exports.getParentCandidates = getParentCandidates;
exports.default = exports.useGatewayLink;
